import * as Yup from 'yup';

export const BANNER_IMAGE_ACCEPT = 'image/jpeg,image/jpg,image/png,image/webp';

export const BANNER_IMAGE_HINT =
  'PNG, JPG, or WebP up to 5MB. Use a landscape rectangle (e.g. 1200×400px): width 600–5000px, height 200–3000px, wider than tall (about 1.2:1 to 4:1).';

export const BANNER_IMAGE_RULES = {
  maxBytes: 5 * 1024 * 1024,
  allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  minWidth: 600,
  maxWidth: 5000,
  minHeight: 200,
  maxHeight: 3000,
  minAspectRatio: 1.2,
  maxAspectRatio: 4,
};

export function readImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Unable to read image'));
    };

    img.src = url;
  });
}

export function getBannerImageDimensionError(width, height) {
  const { minWidth, maxWidth, minHeight, maxHeight, minAspectRatio, maxAspectRatio } =
    BANNER_IMAGE_RULES;
  const aspect = width / height;

  if (width < minWidth || width > maxWidth) {
    return `Image width must be between ${minWidth}px and ${maxWidth}px (selected: ${width}px).`;
  }

  if (height < minHeight || height > maxHeight) {
    return `Image height must be between ${minHeight}px and ${maxHeight}px (selected: ${height}px).`;
  }

  if (aspect < minAspectRatio || aspect > maxAspectRatio) {
    return `Image should be a landscape rectangle (width greater than height), roughly ${minAspectRatio}:1 to ${maxAspectRatio}:1 (selected: ${aspect.toFixed(2)}:1).`;
  }

  return null;
}

export function createBannerImageSchema({ required = false } = {}) {
  let schema = Yup.mixed();

  if (required) {
    schema = schema.required('Banner image is required');
  }

  return schema
    .test('fileSize', 'File size must be less than 5MB', (value) => {
      if (!value) return !required;
      return value.size <= BANNER_IMAGE_RULES.maxBytes;
    })
    .test('fileType', 'Only PNG, JPG, and WebP images are allowed', (value) => {
      if (!value) return !required;
      return BANNER_IMAGE_RULES.allowedTypes.includes(value.type);
    })
    .test('dimensions', 'Invalid image dimensions', async function validateDimensions(value) {
      if (!value) return !required;

      try {
        const { width, height } = await readImageDimensions(value);
        const dimensionError = getBannerImageDimensionError(width, height);

        if (dimensionError) {
          return this.createError({ message: dimensionError });
        }

        return true;
      } catch {
        return this.createError({
          message: 'Unable to read image dimensions. Please upload a valid image file.',
        });
      }
    });
}

export async function validateBannerImageFile(file, { required = false } = {}) {
  try {
    await createBannerImageSchema({ required }).validate(file);
    return null;
  } catch (error) {
    return error.message;
  }
}

export async function handleBannerImageChange(
  event,
  setFieldValue,
  setFieldTouched,
  { required = false, onValidationError } = {}
) {
  const file = event.currentTarget.files?.[0] || null;
  event.target.value = '';
  await setFieldValue('image', file, true);
  setFieldTouched('image', true, false);

  const validationError = await validateBannerImageFile(file, { required });
  if (file && validationError && onValidationError) {
    onValidationError(validationError);
  }
}
